/**
 * RESEARCH SPACES — the Owner's self-service surface.
 *
 * WHAT THIS IS NOT. It is not a second dashboard and not a second research
 * engine. A space page is the persistent identity around the SAME windows,
 * episodes, topics, knowledge and experiments the rest of the workspace already
 * reads. The page adds the mission, the objectives and the evidence links; the
 * records themselves remain where they always were.
 *
 * WHAT IT MUST SAY PLAINLY. A space can be created without developer
 * intervention; its Vision and Mission are the Owner's; its Perspective is the
 * researcher's; an integration authorization is not a credential; and chat does
 * not silently start autonomous research. Each of those is written where the
 * Owner acts on it, not hidden in a report.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { createLoadable } from '../resource';
import { navigate } from '../router';
import { readGateway } from '../gateway';
import { PageHeader, Technical } from './about';
import { Failure } from './failure';
import { formatTurnTime as formatRecordTime } from '../chat';
import { SpaceInstrumentsPanel } from './space-instruments';
import {
	authorizeSpaceRun,
	createResearchSpace,
	listPerspectiveRevisions,
	listResearchSpaces,
	publishSpaceKnowledge,
	readResearchSpace,
	revokeSpaceIntegration,
	runSpaceModelTrial,
	saveSpaceDocument,
	saveSpaceIntegration,
	saveSpaceObjective,
	updateResearchSpace,
	type ResearchSpace,
	type SpaceDetail,
	type SpaceObjective,
} from '../space';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function errorText(error: { message: string } | null | undefined): string {
	return error?.message ?? 'the request failed without a reason';
}

function StatusPill({ status }: { status: string }) {
	return <code class="small">{status}</code>;
}

function Notice({ message }: { message: string | null }) {
	if (message === null) return null;
	return (
		<p class="panel small" role="status" style="padding: 8px 12px;">
			{message}
		</p>
	);
}

function TextField(props: {
	label: string;
	value: string;
	onInput: (value: string) => void;
	placeholder?: string;
	rows?: number;
}) {
	return (
		<label style="display:block; margin: 8px 0;">
			<span class="small muted">{props.label}</span>
			{props.rows === undefined ? (
				<input
					type="text"
					value={props.value}
					placeholder={props.placeholder}
					onInput={(event) => props.onInput((event.target as HTMLInputElement).value)}
					style="display:block; width:100%;"
				/>
			) : (
				<textarea
					value={props.value}
					placeholder={props.placeholder}
					rows={props.rows}
					onInput={(event) => props.onInput((event.target as HTMLTextAreaElement).value)}
					style="display:block; width:100%;"
				/>
			)}
		</label>
	);
}

/** THE DIRECTORY, AND ONE PROGRAMME'S OWN ENVIRONMENT. `/spaces` lists the
 *  workspaces; `/spaces/<id>` is the workspace page. The route owns which one,
 *  so a bookmark and a reload open the same programme. */
export function WorkspacesView() {
	return <SpaceDirectory />;
}

export function WorkspaceView({ spaceId }: { spaceId: string }) {
	return <SpacePage spaceId={spaceId} />;
}

function SpaceDirectory() {
	const spaces = useMemo(() => createLoadable(listResearchSpaces), []);
	const [name, setName] = useState('');
	const [what, setWhat] = useState('');
	const [why, setWhy] = useState('');
	const [outcome, setOutcome] = useState('');
	const [vision, setVision] = useState('');
	const [mission, setMission] = useState('');
	const [objectives, setObjectives] = useState('');
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);

	useEffect(() => {
		void spaces.load();
	}, [spaces]);

	const state = spaces.state.value;

	async function create(): Promise<void> {
		if (busy) return;
		setBusy(true);
		setNotice(null);
		const result = await createResearchSpace({
			name,
			what,
			why,
			outcome,
			...(vision.trim().length === 0 ? {} : { vision: vision.trim() }),
			...(mission.trim().length === 0 ? {} : { mission: mission.trim() }),
			objectives: objectives
				.split('\n')
				.map((line) => line.trim())
				.filter((line) => line.length > 0),
		});
		setBusy(false);
		if (!result.ok) {
			setNotice(errorText(result.error));
			return;
		}
		const created = result.data.space;
		if (created === undefined) {
			setNotice('the space was created but the server returned no identity; reload this page');
			return;
		}
		navigate({ destination: 'spaces', section: created.spaceId, recordId: null });
	}

	if (state.status === 'loading') {
		return (
			<section class="view">
				<PageHeader title="Research Spaces" id="spaces-heading" about={{ purpose: 'Persistent research identities over the one Scout engine.' }} />
				<p class="muted">Reading the spaces…</p>
			</section>
		);
	}
	if (state.status === 'failed' && state.error !== null) {
		return <Failure error={state.error} onRetry={() => void spaces.load()} what="your research spaces" />;
	}
	const list = state.data?.spaces ?? [];
	return (
		<section class="view" aria-labelledby="spaces-heading">
			<PageHeader
				id="spaces-heading"
				title="Research Workspaces"
				about={{
					purpose:
						'A research workspace is a persistent identity over the one Scout engine: an Owner-owned Vision and Mission, a Scout-authored Perspective, objectives with evidence, authorized integrations and its own dedicated conversation.',
					how: (
						<p>
							Open a workspace to see its research topics, documents, authorized windows,
							knowledge and capabilities in one place. Creating one asks what the programme is,
							why it exists and what it hopes to achieve.
						</p>
					),
					limits: (
						<p>
							A workspace is a configuration, not a second model deployment. Its work is
							written to the same runs, episodes, sources, experiments and knowledge records as
							Main Scout's, carrying a space dimension so private work cannot leak between
							workspaces.
						</p>
					),
				}}
			>
				<p class="view-blurb">
					Domain programmes — SYNAAS R&amp;D and the other initiatives — each with their own
					mission, research topics, evidence and operating record.
				</p>
			</PageHeader>
			{state.data?.wired === false && (
				<p class="notice">Research workspaces are not wired in this deployment: {state.data.reason}</p>
			)}
			<section class="panel panel-flush" aria-labelledby="workspace-list-heading">
				<div class="section-head">
					<div>
						<h2 id="workspace-list-heading">Workspaces</h2>
						<p class="section-note">
							{list.length === 0 ? 'None on record' : `${list.length} on record`}
						</p>
					</div>
				</div>
				{list.length === 0 ? (
					<div class="empty-state">
						<p class="empty-title">No research workspace exists yet</p>
						<p class="empty-note">
							A workspace gives a programme its own mission and research identity. Create one
							below when there is a domain to pursue; Main Scout continues to work without one.
						</p>
					</div>
				) : (
					<div class="table-wrap">
						<table class="data-table workspace-table">
							<thead>
								<tr>
									<th scope="col">Workspace</th>
									<th scope="col">Status</th>
									<th scope="col">Schedule</th>
									<th scope="col" class="numeric">Config</th>
									<th scope="col">Created</th>
									<th scope="col" class="cell-action">
										<span class="visually-hidden">Open</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{list.map((space) => (
									<tr key={space.spaceId} class="row-link">
										<td class="cell-title">
											<a
												href={`/spaces/${encodeURIComponent(space.spaceId)}`}
												onClick={(event) => {
													if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
													event.preventDefault();
													navigate({ destination: 'spaces', section: space.spaceId, recordId: null });
												}}
											>
												{space.name}
											</a>
											<span class="cell-sub">{space.spaceId}</span>
										</td>
										<td data-label="Status">
											<StatusPill status={space.status} />
										</td>
										<td class="cell-muted" data-label="Schedule">
											{space.schedule === null
												? 'Manual authorization only'
												: `${space.schedule.mode} — ${DAY_NAMES.filter((_, index) => space.schedule?.days.includes(index)).join(', ')} ${space.schedule.time} ${space.schedule.timezone}`}
										</td>
										<td class="numeric" data-label="Config">
											v{space.configVersion}
										</td>
										<td class="cell-date" data-label="Created">
											{formatRecordTime(space.createdAt)}
										</td>
										<td class="cell-action">
											<a
												class="button ghost small"
												href={`/spaces/${encodeURIComponent(space.spaceId)}`}
												onClick={(event) => {
													if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
													event.preventDefault();
													navigate({ destination: 'spaces', section: space.spaceId, recordId: null });
												}}
											>
												Open
											</a>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</section>
			<details class="panel create-panel">
				<summary>
					<span class="subhead">Create a research workspace</span>
				</summary>
				<div class="create-body">
					<TextField label="Name" value={name} onInput={setName} placeholder="e.g. SYNAAS R&D" />
					<TextField
						label="What is this project?"
						value={what}
						onInput={setWhat}
						rows={3}
						placeholder="The project, in your own words."
					/>
					<TextField
						label="Why does it exist?"
						value={why}
						onInput={setWhy}
						rows={3}
						placeholder="The reason it should exist at all."
					/>
					<TextField
						label="What do you ultimately hope to achieve?"
						value={outcome}
						onInput={setOutcome}
						rows={3}
						placeholder="The outcome you are seeking."
					/>
					<details class="advanced-create">
						<summary class="small muted">Advanced: paste a full Vision / Mission / first objectives</summary>
						<TextField label="Vision (optional, replaces the three answers above)" value={vision} onInput={setVision} rows={6} />
						<TextField label="Mission (optional)" value={mission} onInput={setMission} rows={5} />
						<TextField
							label="First objectives, one per line (optional)"
							value={objectives}
							onInput={setObjectives}
							rows={4}
						/>
					</details>
					<button type="button" class="button" disabled={busy || name.trim().length < 2} onClick={() => void create()}>
						{busy ? 'Creating…' : 'Create research workspace'}
					</button>
					<Notice message={notice} />
				</div>
			</details>
		</section>
	);
}

interface WorkspaceTopic {
	topicId: string;
	title: string;
	summary: string;
	objective: string;
	status: string;
	lastActivityAt: string;
	stats?: {
		journalCount: number;
		knowledgeCount: number;
		episodeCount: number;
		sourceCount: number;
		experimentCount: number;
		openQuestionCount: number;
	} | null;
}

interface WorkspaceTopicsPayload {
	wired: boolean;
	reason?: string;
	topics: WorkspaceTopic[];
}

interface WorkspaceCapability {
	capabilityId: string;
	spaceId: string | null;
	name: string;
	status: string;
	origin: string;
	shared: boolean;
	version: number;
	execution?: { total: number; succeeded: number } | null;
}

interface WorkspaceCapabilitiesPayload {
	ok: boolean;
	wired: boolean;
	reason?: string;
	capabilities: WorkspaceCapability[];
}

function SpacePage({ spaceId }: { spaceId: string }) {
	const detail = useMemo(() => createLoadable(() => readResearchSpace(spaceId)), [spaceId]);
	const topics = useMemo(
		() =>
			createLoadable<WorkspaceTopicsPayload>(() =>
				readGateway<WorkspaceTopicsPayload>(
					`/api/scout/gateway/research/topics?spaceId=${encodeURIComponent(spaceId)}&limit=100`,
				),
			),
		[spaceId],
	);
	const capabilities = useMemo(
		() =>
			createLoadable<WorkspaceCapabilitiesPayload>(() =>
				readGateway<WorkspaceCapabilitiesPayload>('/api/scout/gateway/foundry/capabilities?limit=200'),
			),
		[],
	);
	const [notice, setNotice] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const state = detail.state.value;

	useEffect(() => {
		void detail.load();
		void topics.load();
		void capabilities.load();
	}, [detail, topics, capabilities]);

	async function refresh(message: string | null = null): Promise<void> {
		setNotice(message);
		await detail.load();
	}

	if (state.status === 'loading') {
		return (
			<section class="view">
				<PageHeader title="Research Space" id="space-heading" about={{ purpose: 'A persistent research identity over the one Scout engine.' }} />
				<p class="muted">Reading the space…</p>
			</section>
		);
	}
	if (state.status === 'failed' && state.error !== null) {
		return <Failure error={state.error} onRetry={() => void detail.load()} what="this research space" />;
	}
	const data = state.data;
	if (data === null || data.space === undefined) {
		return (
			<section class="view">
				<PageHeader title="Research Space" id="space-heading" about={{ purpose: 'A persistent research identity over the one Scout engine.' }} />
				<p class="muted">This deployment returned no space record.</p>
			</section>
		);
	}
	const space = data.space;
	const missionLines =
		(data.documents.MISSION?.content ?? '')
			.split('\n')
			.map((line) => line.trim().replace(/^#+\s*/, ''))
			.filter((line) => line.length > 0);
	// A HEADING IS NOT A MISSION. The first substantial sentence is; falling
	// back to the first line only when the document has nothing else keeps the
	// page from printing "RESEARCH MISSION" as if it were the purpose.
	const missionLine =
		missionLines.find((line) => line.length >= 40) ?? missionLines[0] ?? '';

	async function setStatus(status: 'ACTIVE' | 'PAUSED' | 'ARCHIVED'): Promise<void> {
		setBusy(true);
		const result = await updateResearchSpace(spaceId, { status });
		setBusy(false);
		if (!result.ok) {
			setNotice(errorText(result.error));
			return;
		}
		await refresh(`The space is now ${status}.`);
	}

	return (
		<section class="view" aria-labelledby="space-heading">
			<PageHeader
				id="space-heading"
				title={space.name}
				about={{
					purpose: 'A persistent Research Space identity: its mission, evidence, integrations and dedicated researcher.',
					how: (
						<p>
							The researcher reads the Vision, Mission and Perspective on every turn and every
							authorized step. The mission gives direction; it is not a fixed list of topics.
						</p>
					),
					limits: (
						<p>
							Chat is available while the space is ACTIVE or PAUSED. Autonomous research requires
							an ACTIVE space and an explicit run or due schedule. An archived space is read-only.
						</p>
					),
				}}
			>
				<p class="view-blurb">
					{missionLine.length > 0
						? missionLine.slice(0, 320)
						: 'No Mission document is on record for this workspace yet.'}
				</p>
			</PageHeader>
			<div class="panel" style="padding:12px; margin-bottom:16px;">
				<StatusPill status={space.status} />{' '}
				<span class="small muted">
					id <code>{space.spaceId}</code> · config v{space.configVersion} · created {space.createdAt}
				</span>
				{/* W07: the read has always returned the next slot; the page now
				    renders it, so a schedule can be inspected without reading the
				    JSON or asking the CLI. */}
				<div class="small muted" style="margin-top:6px;">
					Next scheduled window:{' '}
					{data.nextSlot === null
						? space.schedule === null || !space.schedule.enabled
							? 'none — manual authorization only'
							: 'not computed'
						: `${data.nextSlot.at} (${data.nextSlot.slot})`}
				</div>
				<div style="display:flex; gap:8px; flex-wrap:wrap; margin-top:10px;">
					<a
						class="button"
						href={`/chat/space/${encodeURIComponent(spaceId)}`}
						onClick={(event) => {
							if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
							event.preventDefault();
							navigate({ destination: 'chat', section: 'space', recordId: spaceId });
						}}
					>
						Open researcher chat
					</a>
					{space.status !== 'ACTIVE' && space.status !== 'ARCHIVED' && (
						<button type="button" class="button ghost" disabled={busy} onClick={() => void setStatus('ACTIVE')}>
							Resume
						</button>
					)}
					{space.status === 'ACTIVE' && (
						<button type="button" class="button ghost" disabled={busy} onClick={() => void setStatus('PAUSED')}>
							Pause
						</button>
					)}
					{space.status !== 'ARCHIVED' && (
						<button type="button" class="button ghost" disabled={busy} onClick={() => void setStatus('ARCHIVED')}>
							Archive
						</button>
					)}
				</div>
				<Notice message={notice} />
			</div>

			<nav class="topic-nav" aria-label="Workspace sections">
				<a href="#workspace-topics">Topics</a>
				<a href="#workspace-capabilities">Capabilities</a>
				<a href="#workspace-documents">Documents</a>
				<a href="#workspace-objectives">Objectives</a>
				<a href="#workspace-research">Research</a>
				<a href="#workspace-integrations">Integrations</a>
				<a href="#workspace-knowledge">Knowledge</a>
				<a href="#workspace-instruments">Instruments</a>
			</nav>

			<section class="panel panel-flush" id="workspace-topics" aria-labelledby="workspace-topics-heading">
				<div class="section-head">
					<div>
						<h2 id="workspace-topics-heading">Research topics</h2>
						<p class="section-note">
							The subjects this workspace's researcher is pursuing — the same canonical topic
							records Research Control lists.
						</p>
					</div>
					<a
						class="link-button"
						href="/research"
						onClick={(event) => {
							if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
							event.preventDefault();
							navigate({ destination: 'research', section: null, recordId: null });
						}}
					>
						Research Control
					</a>
				</div>
				{topics.state.value.status === 'loading' && (
					<p class="notice" role="status">Reading this workspace's topics…</p>
				)}
				{topics.state.value.status === 'failed' && topics.state.value.error !== null && (
					<Failure
						error={topics.state.value.error}
						what="this workspace's topics"
						onRetry={() => void topics.load()}
					/>
				)}
				{topics.state.value.status === 'ready' && (
					<>
						{(topics.state.value.data?.topics ?? []).length === 0 ? (
							<div class="empty-state">
								<p class="empty-title">No research topic is attributed to this workspace</p>
								<p class="empty-note">
									A topic is recorded when Scout opens a subject in a window run by this
									workspace. An empty list means none has been attributed yet, not that the
									workspace has done nothing.
								</p>
							</div>
						) : (
							<div class="table-wrap">
								<table class="data-table">
									<thead>
										<tr>
											<th scope="col">Topic</th>
											<th scope="col">Status</th>
											<th scope="col">Last activity</th>
											<th scope="col" class="numeric">Journal</th>
											<th scope="col" class="numeric">Evidence</th>
											<th scope="col" class="numeric">Open questions</th>
										</tr>
									</thead>
									<tbody>
										{(topics.state.value.data?.topics ?? []).map((topic) => (
											<tr key={topic.topicId} class="row-link">
												<td class="cell-title">
													<a
														href={`/research/topic/${encodeURIComponent(topic.topicId)}`}
														onClick={(event) => {
															if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
															event.preventDefault();
															navigate({ destination: 'research', section: 'topic', recordId: topic.topicId });
														}}
													>
														{topic.title}
													</a>
													<span class="cell-sub">{topic.summary || topic.objective}</span>
												</td>
												<td data-label="Status">
													<span class={`status status-${topic.status.toLowerCase()}`}>{topic.status}</span>
												</td>
												<td class="cell-date" data-label="Last activity">
													{formatRecordTime(topic.lastActivityAt)}
												</td>
												<td class="numeric" data-label="Journal">
													{topic.stats?.journalCount ?? '—'}
												</td>
												<td class="numeric" data-label="Evidence">
													{topic.stats?.sourceCount ?? '—'}
												</td>
												<td class="numeric" data-label="Open questions">
													{topic.stats?.openQuestionCount ?? '—'}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</>
				)}
			</section>

			<section class="panel panel-flush" aria-labelledby="workspace-capabilities-heading">
				<div class="section-head">
					<div>
						<h2 id="workspace-capabilities-heading">Capabilities</h2>
						<p class="section-note">
							Foundry capabilities this workspace owns, plus the shared registry it can call.
							A capability registered elsewhere and not shared is not shown.
						</p>
					</div>
					<a
						class="link-button"
						href="/foundry"
						onClick={(event) => {
							if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
							event.preventDefault();
							navigate({ destination: 'foundry', section: null, recordId: null });
						}}
					>
						Capability Library
					</a>
				</div>
				{capabilities.state.value.status === 'loading' && (
					<p class="notice" role="status">Reading the Foundry registry…</p>
				)}
				{capabilities.state.value.status === 'failed' && capabilities.state.value.error !== null && (
					<Failure
						error={capabilities.state.value.error}
						what="this workspace's capabilities"
						onRetry={() => void capabilities.load()}
					/>
				)}
				{capabilities.state.value.status === 'ready' &&
					(() => {
						const relevant = (capabilities.state.value.data?.capabilities ?? []).filter(
							(capability) => capability.spaceId === spaceId || capability.shared,
						);
						if (relevant.length === 0) {
							return (
								<div class="empty-state">
									<p class="empty-title">No capability is registered to or shared with this workspace</p>
									<p class="empty-note">
										A capability created by this workspace's researcher appears here after it is
										registered; a shared capability appears when the developer promotes it.
									</p>
								</div>
							);
						}
						return (
							<div class="table-wrap">
								<table class="data-table">
									<thead>
										<tr>
											<th scope="col">Capability</th>
											<th scope="col">Origin</th>
											<th scope="col">Scope</th>
											<th scope="col">Status</th>
											<th scope="col" class="numeric">Version</th>
											<th scope="col" class="numeric">Runs ok/total</th>
											<th scope="col" class="cell-action">
												<span class="visually-hidden">Open</span>
											</th>
										</tr>
									</thead>
									<tbody>
										{relevant.map((capability) => (
											<tr
												key={capability.capabilityId}
												class="row-link"
												onClick={(event) => {
													if ((event.target as HTMLElement).closest('a, button') !== null) return;
													navigate({
														destination: 'foundry',
														section: 'capability',
														recordId: capability.capabilityId,
													});
												}}
											>
												<td class="cell-title" data-label="Capability">
													<a
														href={`/foundry/capability/${encodeURIComponent(capability.capabilityId)}`}
														onClick={(event) => {
															if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
															event.preventDefault();
															navigate({
																destination: 'foundry',
																section: 'capability',
																recordId: capability.capabilityId,
															});
														}}
													>
														{capability.name}
													</a>
													<span class="cell-sub">{capability.capabilityId}</span>
												</td>
												<td data-label="Origin">{capability.origin.replaceAll('_', ' ')}</td>
												<td class="cell-muted" data-label="Scope">
													{capability.shared ? 'Shared registry' : 'This workspace'}
												</td>
												<td data-label="Status">
													<span class={`status status-${capability.status.toLowerCase()}`}>
														{capability.status}
													</span>
												</td>
												<td class="numeric" data-label="Version">
													{capability.version}
												</td>
												<td class="numeric" data-label="Runs ok/total">
													{capability.execution === null || capability.execution === undefined
														? '0'
														: `${capability.execution.succeeded}/${capability.execution.total}`}
												</td>
												<td class="cell-action">
													<span class="muted small">Inspect</span>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						);
					})()}
			</section>

			{space.slug === 'synaas-r-d' && (
				<section class="panel synaas-note" aria-labelledby="synaas-heading">
					<div class="section-head">
						<div>
							<span class="eyebrow">Programme context</span>
							<h2 id="synaas-heading">SYNAAS and the Universal Capability Foundry</h2>
						</div>
					</div>
					<p>
						SYNAAS is the intended cross-agent capability-sharing intelligence in the broader
						AAS ecosystem: agents discover, adapt and reuse capabilities within their own
						authorized environments while Scout's Harness provides the registry, sandbox,
						provenance and delivery. This workspace is where that programme's research is
						recorded.
					</p>
					<p class="section-note">
						Scope of this build: the workspace, its research records and Scout's own Foundry
						are real and shown here. Cross-agent federation is an intended integration, not a
						deployed one — no other agent's credentials, registry or data are reachable from
						this deploy.
					</p>
					<a
						class="link-button"
						href="/foundry"
						onClick={(event) => {
							if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
							event.preventDefault();
							navigate({ destination: 'foundry', section: null, recordId: null });
						}}
					>
						Open the Capability Library
					</a>
				</section>
			)}

			<div id="workspace-documents">
				<DocumentsPanel spaceId={spaceId} detail={data} onSaved={(message) => void refresh(message)} />
				<PerspectiveRevisionsPanel spaceId={spaceId} />
			</div>
			<div id="workspace-objectives">
				<ObjectivesPanel spaceId={spaceId} objectives={data.objectives} onSaved={(message) => void refresh(message)} />
			</div>
			<div id="workspace-research">
				<ResearchPanel spaceId={spaceId} detail={data} onStarted={() => void refresh('The window was authorized and is now running.')} />
				<SchedulePanel spaceId={spaceId} space={space} onSaved={(message) => void refresh(message)} />
			</div>
			<div id="workspace-integrations">
				<IntegrationsPanel spaceId={spaceId} detail={data} onSaved={(message) => void refresh(message)} />
			</div>
			<div id="workspace-knowledge">
				<KnowledgePanel spaceId={spaceId} detail={data} onChanged={(message) => void refresh(message)} />
			</div>
			<div id="workspace-instruments">
				<TrialsPanel detail={data} />
				<SpaceInstrumentsPanel
					spaceId={spaceId}
					spaceName={space.name}
					spaceSchedule={space.schedule}
				/>
			</div>
			<details class="technical" style="margin-top:20px;">
				<summary>What the dedicated researcher receives (bounded context)</summary>
				<div class="technical-body">
					<pre style="white-space:pre-wrap;">{data.contextPreview}</pre>
				</div>
			</details>
		</section>
	);
}

function DocumentsPanel(props: {
	spaceId: string;
	detail: SpaceDetail;
	onSaved: (message: string) => void;
}) {
	const layers = ['VISION', 'MISSION', 'PERSPECTIVE'] as const;
	const [layer, setLayer] = useState<(typeof layers)[number]>('VISION');
	const active = props.detail.documents[layer];
	const [content, setContent] = useState(active?.content ?? '');
	const [notice, setNotice] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	useEffect(() => {
		setContent(props.detail.documents[layer]?.content ?? '');
	}, [props.detail, layer]);
	async function save(): Promise<void> {
		setBusy(true);
		const result = await saveSpaceDocument(props.spaceId, { layer, content });
		setBusy(false);
		if (!result.ok) {
			setNotice(errorText(result.error));
			return;
		}
		setNotice(`${layer} version ${result.data.document?.version ?? ''} saved.`);
		props.onSaved(`${layer} saved as a new Owner-authored version.`);
	}
	return (
		<div class="panel" style="padding:16px; margin-bottom:16px;">
			<h2 style="margin-top:0;">The four context layers</h2>
			<p class="small muted">
				Vision and Mission are yours and are versioned. Perspective is the researcher's evolving
				scientific view; writing here records an Owner-authored version. No version is ever silently
				rewritten.
			</p>
			<div style="display:flex; gap:8px; margin:8px 0;">
				{layers.map((entry) => (
					<button
						key={entry}
						type="button"
						class={`button ${entry === layer ? '' : 'ghost'}`}
						onClick={() => setLayer(entry)}
					>
						{entry}
					</button>
				))}
			</div>
			<p class="small muted">
				{active === null
					? `No ACTIVE ${layer} document.`
					: `Version ${active.version}, authored by ${active.authoredBy}, ${active.createdAt}. ${active.provenance}`}
			</p>
			<textarea
				value={content}
				rows={layer === 'PERSPECTIVE' ? 6 : 10}
				onInput={(event) => setContent((event.target as HTMLTextAreaElement).value)}
				style="display:block; width:100%;"
				placeholder={layer === 'VISION' ? 'What this project is, why it exists, what it should become…' : layer === 'MISSION' ? 'The research mission, scope and expected outcomes…' : 'The researcher’s current view…'}
			/>
			<button type="button" class="button" disabled={busy || content.trim().length === 0} onClick={() => void save()} style="margin-top:8px;">
				{busy ? 'Saving…' : `Save ${layer} as a new version`}
			</button>
			<Notice message={notice} />
		</div>
	);
}

function ObjectivesPanel(props: {
	spaceId: string;
	objectives: SpaceObjective[];
	onSaved: (message: string) => void;
}) {
	const [statement, setStatement] = useState('');
	const [status, setStatus] = useState<SpaceObjective['status']>('NOT_TESTED');
	const [evidence, setEvidence] = useState('');
	const [statusNote, setStatusNote] = useState('');
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);
	async function save(): Promise<void> {
		setBusy(true);
		const result = await saveSpaceObjective(props.spaceId, {
			statement,
			status,
			...(statusNote.trim().length === 0 ? {} : { statusNote }),
			evidenceRefs: evidence.split(/[,\n]/).map((entry) => entry.trim()).filter((entry) => entry.length > 0),
		});
		setBusy(false);
		if (!result.ok) {
			setNotice(errorText(result.error));
			return;
		}
		setStatement('');
		setEvidence('');
		setStatusNote('');
		setNotice('Objective recorded.');
		props.onSaved('The objective and its evidence link were recorded.');
	}
	return (
		<div class="panel" style="padding:16px; margin-bottom:16px;">
			<h2 style="margin-top:0;">Objectives and evidence</h2>
			<p class="small muted">
				The statuses are scientific, not decorative: a SUPPORTED hypothesis is not a VALIDATED method,
				and a validated method is not an adopted product. SUPPORTED, REFUTED, VALIDATED and GOAL_ACHIEVED
				require at least one durable evidence reference that resolves to THIS space's work.
			</p>
			{props.objectives.length === 0 ? (
				<p class="muted">No objectives recorded yet.</p>
			) : (
				<ul>
					{props.objectives.map((objective) => (
						<li key={objective.objectiveId} style="margin:8px 0;">
							<StatusPill status={objective.status} /> <strong>{objective.statement}</strong>
							<div class="small muted">
								{objective.objectiveId}
								{objective.statusNote === null ? '' : ` — ${objective.statusNote}`}
							</div>
							<div class="small">
								Evidence: {objective.evidenceRefs.length === 0 ? 'none recorded' : objective.evidenceRefs.join(', ')}
							</div>
						</li>
					))}
				</ul>
			)}
			<details style="margin-top:12px;">
				<summary class="small muted">Add or update an objective</summary>
				<TextField label="Statement" value={statement} onInput={setStatement} rows={3} />
				<label style="display:block; margin:8px 0;">
					<span class="small muted">Status</span>
					<select value={status} onChange={(event) => setStatus((event.target as HTMLSelectElement).value as SpaceObjective['status'])} style="display:block;">
						{(['NOT_TESTED', 'INVESTIGATING', 'SUPPORTED', 'REFUTED', 'VALIDATED', 'GOAL_ACHIEVED', 'BLOCKED'] as const).map((entry) => (
							<option key={entry} value={entry}>{entry}</option>
						))}
					</select>
				</label>
				<TextField label="Evidence references (comma or newline separated: run:, ep:, knowledge:, exp:, src:)" value={evidence} onInput={setEvidence} rows={3} />
				<TextField label="Status note (optional)" value={statusNote} onInput={setStatusNote} />
				<button type="button" class="button" disabled={busy || statement.trim().length === 0} onClick={() => void save()}>
					{busy ? 'Saving…' : 'Record objective'}
				</button>
				<Notice message={notice} />
			</details>
		</div>
	);
}

function ResearchPanel(props: {
	spaceId: string;
	detail: SpaceDetail;
	onStarted: () => void;
}) {
	const [duration, setDuration] = useState('30');
	const [budget, setBudget] = useState(String(props.detail.space.budget?.maxUsdPerRun ?? 1));
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);
	async function start(): Promise<void> {
		setBusy(true);
		setNotice(null);
		const result = await authorizeSpaceRun(props.spaceId, {
			durationMinutes: Number(duration),
			budgetUsd: Number(budget),
		});
		setBusy(false);
		if (!result.ok) {
			setNotice(errorText(result.error));
			return;
		}
		props.onStarted();
	}
	return (
		<div class="panel" style="padding:16px; margin-bottom:16px;">
			<h2 style="margin-top:0;">Research authorization</h2>
			<p class="small muted">
				“Run Research Now” is an explicit Owner authorization. The researcher then chooses its own
				questions and methods inside this mission and budget. Ordinary chat does not start a window.
			</p>
			<div style="display:flex; gap:12px; flex-wrap:wrap; align-items:end;">
				<label class="small muted">
					Duration (minutes)
					<input type="number" min={5} max={480} value={duration} onInput={(event) => setDuration((event.target as HTMLInputElement).value)} />
				</label>
				<label class="small muted">
					Budget (USD)
					<input type="number" min={0} max={100} step="0.01" value={budget} onInput={(event) => setBudget((event.target as HTMLInputElement).value)} />
				</label>
				<button type="button" class="button" disabled={busy || props.detail.space.status !== 'ACTIVE'} onClick={() => void start()}>
					{busy ? 'Authorizing…' : 'Run Research Now'}
				</button>
			</div>
			<Notice message={notice} />
			<h3>Recent windows</h3>
			{props.detail.runs.length === 0 ? (
				<p class="muted">No windows authorized in this space yet.</p>
			) : (
				<ul>
					{props.detail.runs.map((run) => {
						// THE STEP COUNTER IS THE CONTINUOUS LANE'S ACTUAL WORK.
						// `segmentsRun` can be 0 for a window that executed two
						// steps; showing only segments made a real window read as
						// empty. The terminal reason is bounded here because the
						// admission equation is long; the window page carries it.
						const why =
							run.terminalReason === null
								? ''
								: ` — ${
										run.terminalReason.length > 240
											? `${run.terminalReason.slice(0, 240)}…`
											: run.terminalReason
									}`;
						return (
							<li key={run.runId} style="margin:6px 0;">
								<StatusPill status={run.state} />{' '}
								<a href={`/research/window/${encodeURIComponent(run.runId)}`}>{run.runId}</a>{' '}
								<span class="small muted">
									started {run.startedAt}; {run.stepsCompleted ?? 0} step(s) completed;{' '}
									{run.segmentsRun} segment(s); ends {run.deadlineAt}
									{why}
								</span>
							</li>
						);
					})}
				</ul>
			)}
			<h3>Questions and episodes</h3>
			{props.detail.episodes.length === 0 ? (
				<p class="muted">No investigation has been chosen in this space yet.</p>
			) : (
				<ul>
					{props.detail.episodes.map((episode) => (
						<li key={episode.episodeId} style="margin:6px 0;">
							<StatusPill status={episode.status} /> <a href={`/research/observatory/${encodeURIComponent(episode.episodeId)}`}>{episode.episodeId}</a>{' '}
							<span class="small">{episode.question}</span>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

function SchedulePanel(props: {
	spaceId: string;
	space: ResearchSpace;
	onSaved: (message: string) => void;
}) {
	const existing = props.space.schedule;
	const [mode, setMode] = useState(existing?.mode ?? 'MANUAL');
	const [enabled, setEnabled] = useState(existing?.enabled ?? false);
	const [days, setDays] = useState<number[]>(existing?.days ?? [1, 2, 3, 4, 5]);
	const [time, setTime] = useState(existing?.time ?? '09:00');
	const [timezone, setTimezone] = useState(existing?.timezone ?? 'Asia/Kuala_Lumpur');
	const [durationMinutes, setDurationMinutes] = useState(String(existing?.durationMinutes ?? 30));
	const [budgetUsd, setBudgetUsd] = useState(String(existing?.budgetUsd ?? 1));
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);
	async function save(): Promise<void> {
		setBusy(true);
		const result = await updateResearchSpace(props.spaceId, {
			schedule: {
				mode: mode as 'MANUAL' | 'SCHEDULED' | 'MANDATE',
				enabled,
				days,
				time,
				timezone,
				durationMinutes: Number(durationMinutes),
				budgetUsd: Number(budgetUsd),
			},
		});
		setBusy(false);
		if (!result.ok) {
			setNotice(errorText(result.error));
			return;
		}
		setNotice('Schedule saved.');
		props.onSaved('The schedule was saved; the next slot is armed in the cloud scheduler.');
	}
	return (
		<div class="panel" style="padding:16px; margin-bottom:16px;">
			<h2 style="margin-top:0;">Schedule and budget</h2>
			<p class="small muted">
				Scheduling is independent per space. A due slot is claimed once and opened through the same
				run service; a duplicate cloud tick cannot start it twice. The scheduler runs in the cloud,
				without your laptop.
			</p>
			<label class="small muted" style="display:block;">Mode
				<select value={mode} onChange={(event) => setMode((event.target as HTMLSelectElement).value as 'MANUAL' | 'SCHEDULED' | 'MANDATE')} style="display:block;">
					<option value="MANUAL">Manual authorization only</option>
					<option value="SCHEDULED">Scheduled</option>
					<option value="MANDATE">Time-bounded mandate</option>
				</select>
			</label>
			<label class="small" style="display:block; margin:8px 0;">
				<input type="checkbox" checked={enabled} onChange={(event) => setEnabled((event.target as HTMLInputElement).checked)} /> enabled
			</label>
			<div style="display:flex; gap:6px; flex-wrap:wrap;">
				{DAY_NAMES.map((label, index) => (
					<label key={label} class="small">
						<input
							type="checkbox"
							checked={days.includes(index)}
							onChange={(event) => {
								const checked = (event.target as HTMLInputElement).checked;
								setDays((current) => checked ? [...current, index] : current.filter((day) => day !== index));
							}}
						/>{' '}
						{label}
					</label>
				))}
			</div>
			<div style="display:flex; gap:12px; flex-wrap:wrap; margin-top:8px;">
				<label class="small muted">Time <input type="time" value={time} onInput={(event) => setTime((event.target as HTMLInputElement).value)} /></label>
				<label class="small muted">Timezone <input type="text" value={timezone} onInput={(event) => setTimezone((event.target as HTMLInputElement).value)} /></label>
				<label class="small muted">Minutes <input type="number" min={5} max={480} value={durationMinutes} onInput={(event) => setDurationMinutes((event.target as HTMLInputElement).value)} /></label>
				<label class="small muted">Budget USD <input type="number" min={0} max={100} step="0.01" value={budgetUsd} onInput={(event) => setBudgetUsd((event.target as HTMLInputElement).value)} /></label>
			</div>
			<button type="button" class="button" disabled={busy} onClick={() => void save()} style="margin-top:8px;">
				{busy ? 'Saving…' : 'Save schedule'}
			</button>
			<Notice message={notice} />
			<p class="small muted">
				Next slot: {props.space.schedule === null ? 'no schedule' : 'see the space read after saving'}.
				Budget: {props.space.budget === null ? 'not recorded' : `$${props.space.budget.maxUsdPerRun} per run`}.
			</p>
		</div>
	);
}

function IntegrationsPanel(props: {
	spaceId: string;
	detail: SpaceDetail;
	onSaved: (message: string) => void;
}) {
	const [kind, setKind] = useState<'GITHUB_REPOSITORY' | 'MODEL_PROVIDER'>('GITHUB_REPOSITORY');
	const [provider, setProvider] = useState('github');
	const [repo, setRepo] = useState('');
	const [model, setModel] = useState('');
	const [maxUsdPerCall, setMaxUsdPerCall] = useState('0.05');
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);
	const [trialPrompt, setTrialPrompt] = useState('In one sentence, state what a language model can and cannot establish without external evidence.');
	const [trialProvider, setTrialProvider] = useState<'deepseek' | 'gemini'>('deepseek');
	const [trialBusy, setTrialBusy] = useState(false);
	const [trialNotice, setTrialNotice] = useState<string | null>(null);

	async function save(): Promise<void> {
		setBusy(true);
		const config =
			kind === 'GITHUB_REPOSITORY'
				? { repo: repo.trim() }
				: { ...(model.trim().length === 0 ? {} : { model: model.trim() }), maxUsdPerCall: Number(maxUsdPerCall) };
		const result = await saveSpaceIntegration(props.spaceId, {
			kind,
			provider: kind === 'GITHUB_REPOSITORY' ? 'github' : provider,
			config,
		});
		setBusy(false);
		if (!result.ok) {
			setNotice(errorText(result.error));
			return;
		}
		setNotice('Integration authorization saved.');
		props.onSaved('The integration authorization was recorded. A missing deployment credential is reported as NOT CONFIGURED, not as access.');
	}

	async function revoke(integrationId: string): Promise<void> {
		const result = await revokeSpaceIntegration(props.spaceId, integrationId);
		if (!result.ok) {
			setNotice(errorText(result.error));
			return;
		}
		props.onSaved('The integration was revoked.');
	}

	async function trial(): Promise<void> {
		setTrialBusy(true);
		const result = await runSpaceModelTrial(props.spaceId, {
			provider: trialProvider,
			prompt: trialPrompt,
			maxOutputTokens: 200,
		});
		setTrialBusy(false);
		if (!result.ok) {
			setTrialNotice(errorText(result.error));
			return;
		}
		const value = result.data.trial;
		setTrialNotice(
			value === undefined
				? 'No trial record was returned.'
				: `${value.status}: ${value.detail ?? ''} ${value.responseExcerpt === null ? '' : `— ${value.responseExcerpt.slice(0, 500)}`}`,
		);
		props.onSaved('The model trial was recorded as a durable receipt.');
	}

	return (
		<div class="panel" style="padding:16px; margin-bottom:16px;">
			<h2 style="margin-top:0;">Authorized integrations</h2>
			<p class="small muted">
				An integration is an Owner authorization plus a non-secret scope. It is not a credential: when
				the deployment lacks the secret, the state is NOT CONFIGURED and nothing can read or call it.
				The presence of a connector never implies access to every account.
			</p>
			{props.detail.integrations.length === 0 ? (
				<p class="muted">No integrations configured.</p>
			) : (
				<ul>
					{props.detail.integrations.map((integration) => (
						<li key={integration.integrationId} style="margin:8px 0;">
							<StatusPill status={integration.configured ? 'CONFIGURED' : integration.status} />{' '}
							<strong>{integration.kind}</strong> {integration.provider} — {integration.scope}
							<div class="small muted">{integration.reason ?? 'the authorization and the deployment credential are both present'}</div>
							{integration.status !== 'REVOKED' && (
								<button type="button" class="button ghost small" onClick={() => void revoke(integration.integrationId)}>
									Revoke
								</button>
							)}
						</li>
					))}
				</ul>
			)}
			<details style="margin-top:12px;">
				<summary class="small muted">Authorize an integration</summary>
				<label class="small muted" style="display:block; margin:8px 0;">Kind
					<select value={kind} onChange={(event) => setKind((event.target as HTMLSelectElement).value as 'GITHUB_REPOSITORY' | 'MODEL_PROVIDER')} style="display:block;">
						<option value="GITHUB_REPOSITORY">GitHub repository (read-only)</option>
						<option value="MODEL_PROVIDER">Experimental model provider</option>
					</select>
				</label>
				{kind === 'GITHUB_REPOSITORY' ? (
					<TextField label="Repository (owner/name)" value={repo} onInput={setRepo} placeholder="AAS-Forge/aas-forge" />
				) : (
					<>
						<label class="small muted" style="display:block; margin:8px 0;">Provider
							<select value={provider} onChange={(event) => setProvider((event.target as HTMLSelectElement).value)} style="display:block;">
								<option value="deepseek">DeepSeek</option>
								<option value="gemini">Gemini</option>
							</select>
						</label>
						<TextField label="Model (optional; the provider default applies)" value={model} onInput={setModel} placeholder="deepseek-flash" />
						<TextField label="Max USD per call" value={maxUsdPerCall} onInput={setMaxUsdPerCall} />
					</>
				)}
				<button type="button" class="button" disabled={busy} onClick={() => void save()}>
					{busy ? 'Saving…' : 'Authorize'}
				</button>
				<Notice message={notice} />
			</details>
			<details style="margin-top:12px;">
				<summary class="small muted">Run one bounded experimental model trial</summary>
				<label class="small muted" style="display:block; margin:8px 0;">Provider
					<select value={trialProvider} onChange={(event) => setTrialProvider((event.target as HTMLSelectElement).value as 'deepseek' | 'gemini')} style="display:block;">
						<option value="deepseek">DeepSeek</option>
						<option value="gemini">Gemini</option>
					</select>
				</label>
				<TextField label="Prompt" value={trialPrompt} onInput={setTrialPrompt} rows={3} />
				<button type="button" class="button ghost" disabled={trialBusy} onClick={() => void trial()}>
					{trialBusy ? 'Calling the provider…' : 'Run trial (spends within the authorized envelope)'}
				</button>
				<Notice message={trialNotice} />
			</details>
		</div>
	);
}

function KnowledgePanel(props: {
	spaceId: string;
	detail: SpaceDetail;
	onChanged: (message: string) => void;
}) {
	const [notice, setNotice] = useState<string | null>(null);
	async function publish(entryId: string, published: boolean): Promise<void> {
		const result = await publishSpaceKnowledge(props.spaceId, entryId, published);
		if (!result.ok) {
			setNotice(errorText(result.error));
			return;
		}
		setNotice(published ? 'Published to Global Scout Knowledge.' : 'Publication withdrawn.');
		props.onChanged(published ? 'The entry is now published with its origin and limitations.' : 'The entry is private to this space again.');
	}
	return (
		<div class="panel" style="padding:16px; margin-bottom:16px;">
			<h2 style="margin-top:0;">Knowledge</h2>
			<p class="small muted">
				Private space knowledge stays private. Publication is your explicit act; a published entry keeps
				its originating space, evidence, status, confidence and limitations. Publication does not
				authorize production integration.
			</p>
			{props.detail.knowledge.length === 0 ? (
				<p class="muted">No knowledge entries yet.</p>
			) : (
				<ul>
					{props.detail.knowledge.map((entry) => (
						<li key={entry.entryId} style="margin:10px 0;">
							<StatusPill status={`${entry.knowledgeStatus}${entry.publishedToGlobal ? '/PUBLISHED' : ''}`} />{' '}
							<strong>{entry.title}</strong>
							<div class="small">{entry.learned}</div>
							<div class="small muted">
								{entry.entryId} · origin {entry.spaceId ?? 'main'} · limitations: {entry.limitations}
							</div>
							{entry.spaceId === props.spaceId && (
								<button
									type="button"
									class="button ghost small"
									onClick={() => void publish(entry.entryId, entry.publishedToGlobal !== true)}
								>
									{entry.publishedToGlobal ? 'Withdraw publication' : 'Publish to global knowledge'}
								</button>
							)}
						</li>
					))}
				</ul>
			)}
			<Notice message={notice} />
		</div>
	);
}

function TrialsPanel({ detail }: { detail: SpaceDetail }) {
	return (
		<div class="panel" style="padding:16px; margin-bottom:16px;">
			<h2 style="margin-top:0;">Experimental model trials</h2>
			<p class="small muted">
				Each row is a durable receipt. Provider-reported and estimated usage are distinguished; a
				NOT CONFIGURED or NOT AUTHORIZED row means no provider call was made.
			</p>
			{detail.trials.length === 0 ? (
				<p class="muted">No model trials recorded.</p>
			) : (
				<table class="small" style="width:100%; border-collapse:collapse;">
					<thead>
						<tr>
							<th align="left">Trial</th>
							<th align="left">Provider/model</th>
							<th align="left">Status</th>
							<th align="left">Usage</th>
							<th align="left">Cost</th>
						</tr>
					</thead>
					<tbody>
						{detail.trials.map((trial) => (
							<tr key={trial.trialId}>
								<td><code>{trial.trialId}</code></td>
								<td>{trial.provider}/{trial.model}</td>
								<td>{trial.status}</td>
								<td>{trial.inputTokens ?? '—'} in / {trial.outputTokens ?? '—'} out ({trial.usageSource ?? 'none'})</td>
								<td>{trial.costMicrousd === null ? 'unpriceable' : `$${(trial.costMicrousd / 1_000_000).toFixed(6)} envelope`}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
			<Technical>
				<p class="small">
					A trial is TEST_EVIDENCE, never a source. The catalog envelope is not the provider invoice.
				</p>
			</Technical>
		</div>
	);
}

/**
 * THE PERSPECTIVE'S OWN HISTORY — why the researcher's assessment changed.
 * A version number without the evidence that moved it is a revision nobody can
 * audit; this panel reads the durable revision records (change kind, statement
 * and the evidence refs that caused it) rather than reconstructing them.
 */
function PerspectiveRevisionsPanel({ spaceId }: { spaceId: string }) {
	const revisions = useMemo(
		() => createLoadable(() => listPerspectiveRevisions(spaceId)),
		[spaceId],
	);
	useEffect(() => {
		void revisions.load();
	}, [revisions]);
	const state = revisions.state.value;
	return (
		<details class="panel" style="padding:12px; margin-bottom:16px;">
			<summary>
				Perspective revision history
				{state.status === 'ready'
					? ` (${String(state.data?.revisions?.length ?? 0)})`
					: ''}
			</summary>
			{state.status === 'loading' && <p class="muted small">Reading revisions…</p>}
			{state.status === 'failed' && state.error !== null && (
				<p class="small muted">{state.error.message}</p>
			)}
			{state.status === 'ready' && (state.data?.revisions ?? []).length === 0 && (
				<p class="muted small">
					No evidence-linked revision is recorded. A new Perspective version without a
					revision record has no stated cause.
				</p>
			)}
			<ul class="list">
				{(state.data?.revisions ?? []).map((revision) => (
					<li key={revision.revisionId} class="small" style="margin:8px 0;">
						<code>{revision.fromVersion} → {revision.toVersion}</code>{' '}
						<strong>{revision.changeKind}</strong> · {revision.createdAt}
						<div>{revision.statement}</div>
						<div class="muted">Evidence: {revision.evidenceRefs.join(', ')}</div>
					</li>
				))}
			</ul>
		</details>
	);
}
